import { useDictionaryTools, type DictionaryMode } from "contexts/DictionaryToolsContext";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import bookmark from "../../images/dictionary_bookmark.svg";
import Action from "./../formElements/Action/Action";
import { NO_SELECTION_CLASS_NAME } from "./NoTextSelectionMenu";
import "./TextSelectionMenu.css";
import defineImg from "./images/define.svg";
import translateImg from "./images/translate.svg";

const CONTEXT_MENU_WIDTH = 155;
const CONTEXT_MENU_HEIGHT = 100;

type SelectionContext = "page" | "sidebar";

type TextSelectionMenuProps = {
  isContextMenuOpened: boolean;
  positionalParentId: string;
  scrollableParentId: string;
  selectionContext?: SelectionContext;
};

// A completed selection with its computed menu position. null means no menu.
type SelectionMenu = {
  selection: string;
  style: CSSProperties;
};

export default function TextSelectionMenu(props: TextSelectionMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { openSidebar, updateSelectionState } = useDictionaryTools();

  const [menu, setMenu] = useState<SelectionMenu | null>(null);
  const selectionStartedRef = useRef(false);

  useEffect(() => {
    const parent = document.getElementById(props.scrollableParentId);
    if (!parent) {
      return;
    }

    const onSelectStart = () => {
      selectionStartedRef.current = true;
      setMenu(null);
    };

    const onMouseUp = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("." + NO_SELECTION_CLASS_NAME)) {
        // Some components opt out of dictionary selection entirely.
        selectionStartedRef.current = false;
        setMenu(null);
        return;
      }

      const selection = document.getSelection()?.toString().trim() || "";
      if (!selectionStartedRef.current || !selection) {
        return;
      }
      selectionStartedRef.current = false;

      let left = event.pageX;
      if (event.pageX > window.innerWidth - CONTEXT_MENU_WIDTH) {
        left = event.pageX - CONTEXT_MENU_WIDTH - 10;
      }
      let top = event.pageY;
      if (event.pageY > window.innerHeight - CONTEXT_MENU_HEIGHT) {
        top = event.pageY - CONTEXT_MENU_HEIGHT;
      }

      const scrollableParent = document.getElementById(props.scrollableParentId);
      const positionalParent = document.getElementById(props.positionalParentId);
      if (!scrollableParent || !positionalParent) {
        return;
      }

      const relX = left - positionalParent.offsetLeft;
      const relY = top - scrollableParent.offsetTop + scrollableParent.scrollTop;

      setMenu({
        selection,
        style: {
          position: "absolute",
          top: `${relY + 10}px`,
          left: `${relX > 20 ? relX - 20 : relX}px`,
        },
      });
      updateSelectionState(props.selectionContext || "page", true);
    };

    parent.addEventListener("selectstart", onSelectStart);
    parent.addEventListener("mouseup", onMouseUp);
    return () => {
      parent.removeEventListener("selectstart", onSelectStart);
      parent.removeEventListener("mouseup", onMouseUp);
    };
  }, [
    props.scrollableParentId,
    props.positionalParentId,
    props.selectionContext,
    updateSelectionState,
  ]);

  // Close the menu on navigation.
  useEffect(() => {
    setMenu(null);
  }, [location.pathname]);

  const handleAction = (mode: DictionaryMode) => () => {
    if (!menu) {
      return;
    }
    openSidebar(mode, menu.selection);
    setMenu(null);
  };

  if (!menu || !props.isContextMenuOpened) {
    return null;
  }

  const isPhrase = menu.selection.split(" ").length > 1;

  return (
    <div
      className="selection-context-menu"
      style={menu.style}
      // Keep the page's text selection highlighted while interacting with the menu.
      onMouseDown={event => event.preventDefault()}
    >
      <Action className="btn action-with-image" onClick={handleAction("define")}>
        <img src={defineImg} height={30} width={30} alt="define" />
        <span>{t("Common.DEFINE")}</span>
      </Action>
      <span className="vertical-separator"></span>
      <Action className="btn action-with-image" onClick={handleAction("translate")}>
        <img src={translateImg} height={30} width={30} alt="translate" />
        <span>{t("Common.TRANSLATE")}</span>
      </Action>
      <span className="vertical-separator"></span>
      <Action className="btn action-with-image" onClick={handleAction("save")}>
        <img src={bookmark} height={30} width={30} alt="save word" />
        <span>{t(`Common.SAVE ${isPhrase ? "PHRASE" : "WORD"}`)}</span>
      </Action>
    </div>
  );
}
